/**
 * Check whether a subject (channel, post, note, organization, account, …) grants
 * the given action via its `allowedActions` field — the standard authorization
 * shape Phil's permissions rebuild exposes on every authz-capable entity.
 *
 * Why this exists: call sites should not be reaching into `.allowedActions` and
 * calling `.includes('viewComments')` directly. Going through this helper means
 * (1) action strings are type-narrowed against the subject's own allowed-action
 * union — typos like `'viewComment'` are compile errors — and (2) we get
 * nullish-safe behavior for free, so loading/missing subjects deny by default.
 *
 * The helper is generic over the action type, so it works for ChannelAction,
 * PostAction, NoteAction, OrganizationAction, AccountAction — anything whose
 * GraphQL type exposes `allowedActions: [SomeAction!]!`.
 *
 * `NoInfer` on `action` is what makes (1) hold: `TAction` appears covariantly in
 * both parameters, so without it TypeScript unions the inference candidates and
 * a typo widens the union to `ChannelAction | 'viewComment'` instead of failing.
 * `TAction` must therefore be inferred from the subject alone.
 *
 * @example
 *   if (!canPerform(channel, 'viewComments')) return <Navigate to="/" />
 *   {canPerform(channel, 'manageComments') && <DismissButton />}
 */
export function canPerform<TAction extends string>(
  subject: { allowedActions?: readonly TAction[] | null } | null | undefined,
  action: NoInfer<TAction>,
): boolean {
  return subject?.allowedActions?.includes(action) ?? false
}
